/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { SelectField } from '@app/components/form-fields/SelectField';
import { TextField } from '@app/components/form-fields/TextField';
import useMe from '@app/hooks/auth/useMe';
import useBrands from '@app/hooks/brands/useBrands';
import useChannelPartners from '@app/hooks/channelPartners/useChannelPartners';
import useLocationList from '@app/hooks/locations/useLocations';
import useRoles from '@app/hooks/roles/useRoles';
import type { Brand, ChannelPartner, Client, Location, Role } from '@app/services/api.types';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ModalProps } from 'antd';
import React, { useEffect, useMemo, useRef } from 'react';
import type { UseFormReset } from 'react-hook-form';
import { Controller, useForm, useWatch } from 'react-hook-form';
import * as z from 'zod';

const getRoleRank = (roleName?: string | null) => {
    if (!roleName) return 0;
    const rn = roleName.toLowerCase();
    if (rn === 'super admin') return 3;
    if (rn === 'admin') return 2;
    if (rn === 'brand admin' || rn === 'channel partner admin' || rn === 'location admin') return 1;
    return 0;
};

const baseSchemaShape = {
    first_name: z.string().trim().min(1, 'First name is required'),
    last_name: z.string().trim().min(1, 'Last name is required'),
    email: z
        .string()
        .trim()
        .min(1, 'Email Address is required')
        .email('Email Address must be valid format'),
    role_id: z.string().min(1, 'Role is required'),
    telephone: z.string().trim().min(1, 'Telephone is required'),
};

function getFieldSchema(
    role: Role | null | undefined,
    isEdit: boolean,
    adminName: string,
    errorMsg: string
) {
    if (role?.is_internal) return z.array(z.string()).optional();
    if (role?.name === adminName && isEdit) return z.array(z.string()).min(1, errorMsg);
    return z.array(z.string()).optional();
}

const getSchema = (selectedRole?: Role | null, isEdit?: boolean) =>
    z.object({
        ...baseSchemaShape,
        brand_ids: getFieldSchema(selectedRole, !!isEdit, 'Brand Admin', 'Brand is required'),
        channel_ids: getFieldSchema(
            selectedRole,
            !!isEdit,
            'Channel Partner Admin',
            'Channel is required'
        ),
        location_ids: getFieldSchema(
            selectedRole,
            !!isEdit,
            'Location Admin',
            'Location is required'
        ),
    });

export type UserFormValues = z.infer<ReturnType<typeof getSchema>>;

export type UserFormProps = ModalProps & {
    data?: Client;
    roles?: Role[];
    isEdit?: boolean;
    brands?: Brand[];
    channels?: ChannelPartner[];
    locations?: Location[];
    onSubmit: (data: UserFormValues, reset: UseFormReset<UserFormValues>) => void;
};

export const ClientForm: React.FC<UserFormProps> = ({
    onCancel,
    onSubmit,
    isEdit,
    data,
    ...props
}) => {
    const { data: me } = useMe();
    const { data: brands, isLoading: brandsLoading } = useBrands({ page: 1, per_page: 1000 });
    const { data: locations, isLoading: locationsLoading } = useLocationList({
        page: 1,
        per_page: 1000,
    });
    const { data: roles, isLoading: rolesLoading } = useRoles({ is_internal: 0 });
    const { isLoading: channelPartnersLoading, data: channels } = useChannelPartners({
        page: 1,
        per_page: 1000,
    });

    const isCurrentUser = me?.id === data?.id;

    const schemaRef = useRef(getSchema(undefined, !!isEdit));
    const dynamicResolver = async (values: any, context: any, options: any) => {
        return zodResolver(schemaRef.current)(values, context, options);
    };

    const {
        handleSubmit,
        control,
        reset,
        getValues,
        trigger,
        formState: { isValid, isSubmitting },
    } = useForm<UserFormValues>({
        mode: 'all',
        resolver: dynamicResolver,
        defaultValues: {
            first_name: '',
            last_name: '',
            email: '',
            role_id: '',
            brand_ids: [],
            channel_ids: [],
            location_ids: [],
            telephone: '',
        },
    });

    const watchedRoleId = useWatch({ control, name: 'role_id' }) as string | undefined;
    const selectedRoleId = watchedRoleId ?? data?.role_id;
    const selectedRole = roles?.data?.find((r) => r.id === selectedRoleId) ?? null;
    const meRole = roles?.data?.find((r) => r.id === me?.role_id) ?? null;
    const meRank = getRoleRank(meRole?.name);

    const schema = useMemo(
        () => getSchema(selectedRole ?? undefined, !!isEdit),
        [selectedRole, isEdit]
    );

    useEffect(() => {
        const currentValues = getValues();
        reset(currentValues as Partial<UserFormValues>);
    }, [schema, getValues, reset]);

    useEffect(() => {
        if (selectedRole?.is_internal) {
            const current = getValues();
            reset({
                ...current,
                brand_ids: [],
                channel_ids: [],
                location_ids: [],
            } as Partial<UserFormValues>);
        }
    }, [selectedRole, getValues, reset]);

    useEffect(() => {
        if (!data) {
            reset();
            return;
        }
        const incomingRole = roles?.data?.find((r) => r.id === data.role_id) ?? null;
        if (incomingRole?.is_internal && isEdit) {
            const { brand_ids, channel_ids, location_ids, ...rest } = data as any;
            reset({
                ...rest,
                brand_ids: [],
                channel_ids: [],
                location_ids: [],
            } as Partial<UserFormValues>);
            return;
        }
        reset({
            ...data,
            brand_ids: data.brand_ids ?? [],
            channel_ids: data.channel_ids ?? [],
            location_ids: data.location_ids ?? [],
        } as unknown as UserFormValues);
    }, [data, roles?.data, isEdit, reset]);

    const roleSelectOptions = useMemo(() => {
        return roles?.data?.reduce(
            (acc: { label: string; value: string; disabled: boolean }[], r) => {
                const rank = getRoleRank(r.name);
                const isHigherThanMe = rank > meRank;
                const roleIsEditingAndTarget = isEdit && r.id === data?.role_id;

                if (!isHigherThanMe || roleIsEditingAndTarget) {
                    acc.push({
                        label: r.name,
                        value: r.id,
                        disabled: isHigherThanMe,
                    });
                }
                return acc;
            },
            []
        );
    }, [roles?.data, meRank, isEdit, data?.role_id]);

    const showBrandSelect = useMemo(() => {
        if (!selectedRole) return true;
        if (selectedRole.is_internal) return false;
        const rn = selectedRole.name?.toLowerCase();
        if (rn === 'brand admin') return true;
        if (rn === 'channel partner admin' || rn === 'location admin') return false;

        return true;
    }, [selectedRole]);

    const showChannelSelect = useMemo(() => {
        if (!selectedRole) return true;
        if (selectedRole.is_internal) return false;
        const rn = selectedRole.name?.toLowerCase();
        if (rn === 'channel partner admin') return true;
        if (rn === 'brand admin' || rn === 'location admin') return false;
        return true;
    }, [selectedRole]);

    const showLocationSelect = useMemo(() => {
        if (!selectedRole) return true;
        if (selectedRole.is_internal) return false;
        const rn = selectedRole.name?.toLowerCase();
        if (rn === 'location admin') return true;
        if (rn === 'brand admin' || rn === 'channel partner admin') return false;
        return true;
    }, [selectedRole]);

    useEffect(() => {
        const current = getValues();
        const toClear: Partial<UserFormValues> = {};
        if (!showBrandSelect) toClear.brand_ids = [];
        if (!showChannelSelect) toClear.channel_ids = [];
        if (!showLocationSelect) toClear.location_ids = [];

        if (Object.keys(toClear).length > 0) {
            reset({ ...(current as any), ...toClear } as Partial<UserFormValues>);
        }
    }, [showBrandSelect, showChannelSelect, showLocationSelect, getValues, reset]);

    useEffect(() => {
        const doUpdate = async () => {
            schemaRef.current = getSchema(selectedRole ?? undefined, !!isEdit);

            const current = getValues();
            const toClear: Partial<UserFormValues> = {};
            if (!showBrandSelect) toClear.brand_ids = [];
            if (!showChannelSelect) toClear.channel_ids = [];
            if (!showLocationSelect) toClear.location_ids = [];

            if (Object.keys(toClear).length > 0) {
                reset({ ...(current as any), ...toClear } as Partial<UserFormValues>);
            } else {
                reset(current as Partial<UserFormValues>);
            }

            await trigger();
        };

        doUpdate();
    }, [
        watchedRoleId,
        selectedRole,
        showBrandSelect,
        showChannelSelect,
        showLocationSelect,
        getValues,
        reset,
        trigger,
        isEdit,
    ]);

    return (
        <form onSubmit={handleSubmit((values) => onSubmit(values, reset))} id="user-edit-form">
            <div className="form-fields-wrapper">
                <Controller
                    control={control}
                    name="first_name"
                    render={({ field, fieldState }) => (
                        <TextField
                            fieldState={fieldState}
                            {...field}
                            label="First name"
                            type="text"
                            placeholder="Enter first name"
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="last_name"
                    render={({ field, fieldState }) => (
                        <TextField
                            fieldState={fieldState}
                            {...field}
                            label="Last name"
                            type="text"
                            placeholder="Enter last name"
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="email"
                    render={({ field, fieldState }) => (
                        <TextField
                            fieldState={fieldState}
                            {...field}
                            disabled={isEdit || isCurrentUser}
                            label="Email"
                            type="email"
                            placeholder="Enter email"
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="telephone"
                    render={({ field, fieldState }) => (
                        <TextField
                            fieldState={fieldState}
                            {...field}
                            label="Telephone"
                            type="text"
                            placeholder="Enter telephone"
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="role_id"
                    render={({ field, fieldState }) => (
                        <SelectField
                            fieldState={fieldState}
                            {...field}
                            label="Role"
                            disabled={isCurrentUser}
                            allowClear
                            loading={rolesLoading}
                            placeholder="Select role"
                            options={roleSelectOptions}
                        />
                    )}
                />

                {showBrandSelect && (
                    <Controller
                        control={control}
                        name="brand_ids"
                        render={({ field, fieldState }) => (
                            <SelectField
                                fieldState={fieldState}
                                {...field}
                                value={field.value?.[0] ?? undefined}
                                onChange={(val: any) =>
                                    field.onChange(val ? [val] : [])
                                }
                                label="Brand"
                                allowClear
                                loading={brandsLoading}
                                placeholder="Select brand"
                                options={
                                    brands?.data.map((b) => ({
                                        label: b.name,
                                        value: b.id.toString(),
                                    })) || []
                                }
                            />
                        )}
                    />
                )}

                {showChannelSelect && (
                    <Controller
                        control={control}
                        name="channel_ids"
                        render={({ field, fieldState }) => (
                            <SelectField
                                fieldState={fieldState}
                                {...field}
                                value={field.value?.[0] ?? undefined}
                                onChange={(val: any) =>
                                    field.onChange(val ? [val] : [])
                                }
                                label="Channel"
                                allowClear
                                loading={channelPartnersLoading}
                                placeholder="Select channel"
                                options={
                                    channels?.data.map((c) => ({
                                        label: c.name,
                                        value: c.id.toString(),
                                    })) || []
                                }
                            />
                        )}
                    />
                )}

                {showLocationSelect && (
                    <Controller
                        control={control}
                        name="location_ids"
                        render={({ field, fieldState }) => (
                            <SelectField
                                fieldState={fieldState}
                                {...field}
                                mode="multiple"
                                label="Location"
                                loading={locationsLoading}
                                allowClear
                                placeholder="Select location"
                                options={
                                    locations?.data.map((l) => ({
                                        label: l.name,
                                        value: l.id.toString(),
                                    })) || []
                                }
                            />
                        )}
                    />
                )}
            </div>

            <div className="ant-modal-footer">
                <StyledButtonGroup justify="flex-end">
                    <StyledButton
                        onClick={(e) => onCancel?.(e as React.MouseEvent<HTMLButtonElement>)}
                        color="black"
                        data-testid="reset-button">
                        Cancel
                    </StyledButton>
                    <StyledButton
                        htmlType="submit"
                        type="primary"
                        disabled={!isValid}
                        loading={isSubmitting || props.confirmLoading}
                        data-testid="register-user-button">
                        Save
                    </StyledButton>
                </StyledButtonGroup>
            </div>
        </form>
    );
};

export default ClientForm;
